import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { MessageCircle, Printer, PenTool, Download, CheckCircle, Star, Zap, Shield, Users } from 'lucide-react';

const LandingPage = () => {
  const [activeService, setActiveService] = useState(null);

  // Mock data - will be replaced with backend integration
  const services = [
    {
      id: 'printing',
      title: '3D Print Service',
      description: 'Van prototype tot productie - wij printen jouw ideeën met precisie en expertise',
      icon: Printer,
      features: ['Verschillende materialen', 'Hoogwaardige afwerking', 'Snelle doorlooptijd', 'Professionele kwaliteit'],
      price: 'Vanaf €15'
    },
    {
      id: 'design',
      title: 'Ontwerpservice',
      description: 'Van idee tot bestand tot product - onze ervaren designers maken je visie werkelijkheid',
      icon: PenTool,
      features: ['Concept ontwikkeling', 'Technische tekeningen', '3D visualisatie', 'Ontwerp optimalisatie'],
      price: 'Vanaf €75'
    },
    {
      id: 'files',
      title: 'Digitale Bestanden',
      description: 'Kant-en-klare 3D modellen voor miniaturen, maquettes en prototypes',
      icon: Download,
      features: ['STL bestanden', 'Print-ready formaten', 'Directe download', 'Technische ondersteuning'],
      price: 'Vanaf €5'
    }
  ];

  const benefits = [
    {
      icon: CheckCircle,
      title: 'Precisie & Expertise',
      description: 'Jarenlange ervaring met de nieuwste 3D printtechnologie'
    },
    {
      icon: Zap,
      title: 'Snelle Prototyping',
      description: 'Van idee tot fysiek prototype in enkele dagen'
    },
    {
      icon: Users,
      title: 'Maatwerk Oplossingen',
      description: 'Elke klant krijgt persoonlijke begeleiding en advies'
    },
    {
      icon: Shield,
      title: '100% Tevredenheidsgarantie',
      description: 'Niet tevreden? Dan printen we opnieuw of krijg je je geld terug'
    }
  ];

  const testimonials = [
    {
      name: 'Marco van der Berg',
      company: 'TechStart B.V.',
      text: 'Geweldige service! Ze hebben ons prototype binnen 3 dagen perfect geleverd. De kwaliteit overtrof onze verwachtingen.',
      rating: 5
    },
    {
      name: 'Lisa Janssen',
      company: 'Creative Agency Plus',
      text: 'Van idee tot eindproduct in één week. Het team denkt mee en levert altijd topkwaliteit. Aanrader!',
      rating: 5
    }
  ];

  const whatsappLink = 'https://wa.me/+31648315093';
  const whatsappMessage = encodeURIComponent('Hallo! Ik heb interesse in jullie 3D print services. Kunnen jullie me meer informatie geven?');

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-orange-400 to-orange-600 rounded-lg flex items-center justify-center">
                <Printer className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-xl font-bold text-gray-900">Interactive Visuals 3D</h1>
            </div>
            <Button 
              onClick={() => window.open(`${whatsappLink}?text=${whatsappMessage}`, '_blank')}
              className="bg-[#25d366] hover:bg-[#1ebe5d] text-white font-semibold px-6 py-2 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Start via WhatsApp
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-orange-100 via-orange-50 to-transparent"></div>
        <div className="relative max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-200 px-3 py-1">
                  Van idee tot eindproduct
                </Badge>
                <h1 className="text-4xl md:text-6xl font-bold text-gray-900 leading-tight">
                  Laat jouw <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-orange-600">ideeën</span> tot leven komen
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  Experts in 3D printen en design. Van prototype tot productie, wij begeleiden je van concept tot concreet product met precisie en vakmanschap.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  onClick={() => window.open(`${whatsappLink}?text=${whatsappMessage}`, '_blank')}
                  className="bg-[#25d366] hover:bg-[#1ebe5d] text-white font-semibold px-8 py-4 text-lg rounded-lg transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Start je project via WhatsApp
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl transform rotate-2 hover:rotate-0 transition-transform duration-500">
                <img 
                  src="https://images.unsplash.com/photo-1642969164999-979483e21601?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHwzRCUyMHByaW50aW5nfGVufDB8fHx8MTc1OTc2MDM3Nnww&ixlib=rb-4.1.0&q=85"
                  alt="Professionele 3D printer in actie"
                  className="w-full h-96 object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-white rounded-lg p-4 shadow-xl">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium text-gray-700">Live aan het printen</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Onze <span className="text-orange-600">Diensten</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Van simpele prints tot complexe ontwerpprojecten - wij hebben de expertise om jouw visie werkelijkheid te maken
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service) => {
              const Icon = service.icon;
              return (
                <Card 
                  key={service.id}
                  className="group hover:shadow-xl transition-all duration-300 cursor-pointer border-2 hover:border-orange-200"
                  onMouseEnter={() => setActiveService(service.id)}
                  onMouseLeave={() => setActiveService(null)}
                >
                  <CardHeader className="text-center space-y-4">
                    <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center transition-all duration-300 ${
                      activeService === service.id 
                        ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white' 
                        : 'bg-orange-100 text-orange-600 group-hover:bg-orange-200'
                    }`}>
                      <Icon className="w-8 h-8" />
                    </div>
                    <CardTitle className="text-xl font-bold">{service.title}</CardTitle>
                    <Badge variant="secondary" className="bg-orange-100 text-orange-800">
                      {service.price}
                    </Badge>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <CardDescription className="text-center text-gray-600">
                      {service.description}
                    </CardDescription>
                    <ul className="space-y-2">
                      {service.features.map((feature, index) => (
                        <li key={index} className="flex items-center text-sm text-gray-700">
                          <CheckCircle className="w-4 h-4 mr-2 text-green-500 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-orange-50 to-orange-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
                Waarom kiezen voor <span className="text-orange-600">Interactive Visuals 3D?</span>
              </h2>
              <div className="space-y-6">
                {benefits.map((benefit, index) => {
                  const Icon = benefit.icon;
                  return (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                        <p className="text-gray-600">{benefit.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="space-y-4">
              <img 
                src="https://images.unsplash.com/photo-1611505982706-9ebc79e5d3f1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHw0fHwzRCUyMHByaW50aW5nfGVufDB8fHx8MTc1OTc2MDM3Nnww&ixlib=rb-4.1.0&q=85"
                alt="Industriële 3D printer"
                className="w-full rounded-xl shadow-lg"
              />
              <img 
                src="https://images.unsplash.com/photo-1624066969649-fb75b5a7cc97?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxwcm90b3R5cGVzfGVufDB8fHx8MTc1OTc3OTE1OHww&ixlib=rb-4.1.0&q=85"
                alt="Architecturaal model"
                className="w-full rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Wat klanten <span className="text-orange-600">zeggen</span>
            </h2>
            <p className="text-xl text-gray-600">
              Ontdek waarom bedrijven ons vertrouwen voor hun 3D printprojecten
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-gradient-to-br from-white to-orange-50 border-orange-200">
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <blockquote className="text-gray-700 mb-4 italic">
                    "{testimonial.text}"
                  </blockquote>
                  <footer>
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.company}</div>
                  </footer>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-orange-50 to-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Veelgestelde <span className="text-orange-600">Vragen</span>
            </h2>
          </div>
          
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="bg-white rounded-lg px-6 border border-orange-200">
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-orange-600">
                Hoe lang duurt het voordat mijn print klaar is?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 pt-2">
                De doorlooptijd hangt af van de complexiteit en grootte van je project. Eenvoudige prints zijn vaak binnen 1-2 dagen klaar, complexere projecten kunnen 3-7 dagen duren. We houden je altijd op de hoogte van de voortgang.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-2" className="bg-white rounded-lg px-6 border border-orange-200">
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-orange-600">
                Welke materialen kunnen jullie printen?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 pt-2">
                We werken met verschillende materialen zoals PLA, ABS, PETG, TPU en zelfs metaal-gevulde filamenten. Ook kunnen we printen in verschillende kleuren en afwerkingen. Neem contact op voor specifieke materiaaleisen.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-3" className="bg-white rounded-lg px-6 border border-orange-200">
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-orange-600">
                Ik heb alleen een idee, geen 3D bestand. Kunnen jullie helpen?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 pt-2">
                Absoluut! Ons ontwerpteam kan je van idee tot eindproduct begeleiden. We maken technische tekeningen, 3D modellen en optimaliseren het ontwerp voor 3D printen. Van schets tot product, wij regelen het allemaal.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-4" className="bg-white rounded-lg px-6 border border-orange-200">
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-orange-600">
                Wat als ik niet tevreden ben met het resultaat?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 pt-2">
                We bieden een 100% tevredenheidsgarantie. Niet tevreden met de kwaliteit? Dan printen we opnieuw volgens je feedback of krijg je je geld terug. Kwaliteit staat bij ons voorop.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-orange-500 to-orange-600 text-white">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-3xl md:text-4xl font-bold">
            Klaar om je project te starten?
          </h2>
          <p className="text-xl text-orange-100">
            Neem vandaag nog contact op en ontdek hoe wij jouw ideeën tot leven kunnen brengen
          </p>
          <div className="flex justify-center">
            <Button 
              size="lg"
              onClick={() => window.open(`${whatsappLink}?text=${whatsappMessage}`, '_blank')}
              className="bg-[#25d366] hover:bg-[#1ebe5d] text-white font-semibold px-8 py-4 text-lg rounded-lg transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Start via WhatsApp
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-r from-orange-400 to-orange-600 rounded-lg flex items-center justify-center">
                  <Printer className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-bold">Interactive Visuals 3D</h3>
              </div>
              <p className="text-gray-400">
                Van idee tot eindproduct - experts in 3D printen en design sinds 2020.
              </p>
            </div>
            
            <div className="space-y-4">
              <h4 className="text-lg font-semibold">Diensten</h4>
              <ul className="space-y-2 text-gray-400">
                <li>3D Print Service</li>
                <li>Ontwerpservice</li>
                <li>Digitale Bestanden</li>
                <li>Prototyping</li>
              </ul>
            </div>
            
            <div className="space-y-4">
              <h4 className="text-lg font-semibold">Contact</h4>
              <div className="text-gray-400 space-y-2">
                <p>WhatsApp: +31 6 483 150 93</p>
                <p>Email: info@interactivevisuals3d.nl</p>
                <p>Werkdagen: Ma-Vr 9:00-17:00</p>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Interactive Visuals 3D Service. Alle rechten voorbehouden.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;